// // Custom Autocomplete component using Shadcn UI with minor adjustments, refer: https://www.armand-salle.fr/post/autocomplete-select-shadcn-ui/
import { cn } from "@/utils/common.helpers";
import { Command as CommandPrimitive } from "cmdk";
import { Check } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { ClientPortal } from "./ClientPortal";
import {
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Skeleton } from "./ui/skeleton";

export type Option = Record<"value" | "label", string> & Record<string, string>;

type AutoCompleteProps = {
  options: Option[];
  emptyMessage?: string;
  value?: Option;
  onValueChange?: (value: Option) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onInputChange?: (value: Option) => void;
  onOptionSelect?: (value: Option) => void;
  showNoResult?: boolean;
  icon?: React.ReactNode;
  onEnter?: (value: Option) => void; // New prop
  showSelectedItem?: boolean;
  listItemLeftIcon?: React.ReactNode;
  animatedHints?: string[];
  hasBlackBackground?: boolean;
};

export const AutoComplete = ({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  onFocus,
  onBlur,
  isLoading = false,
  showNoResult = true,
  showSelectedItem = false,
  onInputChange,
  onOptionSelect,
  onEnter,
  icon,
  listItemLeftIcon,
  animatedHints,
  hasBlackBackground = false,
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option>(value as Option);
  const [inputValue, setInputValue] = useState<string>(value?.label || "");
  const [isFocused, setIsFocused] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [currentHintIndex, setCurrentHintIndex] = useState(0);

  useEffect(() => {
    if (isFocused || !animatedHints) return; // Stop hint animation when input is focused

    const interval = setInterval(() => {
      setAnimate(true); // Trigger animation
      setTimeout(() => {
        setAnimate(false); // Reset animation
        setCurrentHintIndex((prev) => (prev + 1) % animatedHints.length); // Cycle to the next hint
      }, 500); // Match this duration to the CSS animation timing
    }, 2000); // Change hint every 2 seconds

    return () => clearInterval(interval);
  }, [animatedHints?.length, isFocused]);

  useEffect(() => {
    if (dropdownRef.current && inputContainerRef.current) {
      const inputRect = inputContainerRef.current.getBoundingClientRect();
      dropdownRef.current.style.top = `${inputRect.bottom - 6}px`;
      dropdownRef.current.style.left = `${inputRect.left}px`;
      if (inputRect.width > 0) {
        dropdownRef.current.style.width = `${inputRect.width}px`;
      }
    }
  }, [isOpen, inputValue]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setOpen(true);
      }

      // This is not a default behaviour of the <input /> field
      if (event.key === "Enter" && input.value !== "") {
        event.preventDefault(); // Prevent default selection behavior of the dropdown
        const optionToSelect = {
          label: input.value,
          value: input.value,
        };
        if (optionToSelect) {
          handleSelectOption(optionToSelect);
          onValueChange?.(optionToSelect);
          if (onEnter) onEnter(optionToSelect);
        }
      }

      if (event.key === "Escape") {
        input.blur();
      }
    },
    [isOpen, options, onValueChange],
  );

  const handleBlur = useCallback(() => {
    setOpen(false);
    setIsFocused(false);
    if (onBlur) onBlur();
  }, [selected, onBlur, setOpen]);

  const handleFocus = useCallback(() => {
    setOpen(true);
    setIsFocused(true);
    if (onFocus) onFocus();
  }, [selected, onFocus, setOpen]);

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      setInputValue(selectedOption.label);

      setSelected(selectedOption);
      onValueChange?.(selectedOption);

      if (onOptionSelect) onOptionSelect(selectedOption);

      // This is a hack to prevent the input from being focused after the user selects an option
      // We can call this hack: "The next tick"
      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
    },
    [onValueChange],
  );

  return (
    <CommandPrimitive onKeyDown={handleKeyDown} shouldFilter={false}>
      <div className="relative z-[2] overflow-hidden" ref={inputContainerRef}>
        <CommandInput
          ref={inputRef}
          value={inputValue}
          onValueChange={
            isLoading
              ? undefined
              : (e) => {
                  setInputValue(e);
                  if (onInputChange)
                    onInputChange({
                      label: e,
                      value: e,
                    });
                }
          }
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={isFocused ? placeholder : ""}
          disabled={disabled}
          className="h-9 text-sm"
          icon={icon}
        />
        {animatedHints && !isFocused && inputValue === "" && (
          <div className="text-new-grey5 pointer-events-none absolute left-2 top-0 flex h-full w-full items-center truncate pl-8 text-sm">
            <span
              className={`hint transition-transform duration-500 ${
                animate ? "search-hint-animate-out" : "search-hint-animate-in"
              }`}
              key={currentHintIndex}
            >
              {animatedHints[currentHintIndex]}
            </span>
          </div>
        )}
      </div>
      {/* Portal is used to ensure options are visible even when the parent has overflow hidden */}
      <ClientPortal selector="body">
        <div ref={dropdownRef} className="absolute z-[2] min-w-[200px]">
          <div
            className={cn(
              "rounded-b-lg border border-t-0 border-white/20 animate-in fade-in-0 zoom-in-95",
              hasBlackBackground ? "bg-black" : "bg-stg-secondary-cta",
              isOpen && options.length > 0 ? "block" : "hidden",
            )}
          >
            <CommandList className="rounded-lg ring-slate-200">
              {isLoading ? (
                <CommandPrimitive.Loading>
                  <div className="p-1">
                    <Skeleton className="h-8 w-full" />
                  </div>
                </CommandPrimitive.Loading>
              ) : null}
              {options.length > 0 && !isLoading ? (
                <CommandGroup>
                  {options.map((option) => {
                    const isSelected = selected?.value === option.value;
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onSelect={() => handleSelectOption(option)}
                        className={cn(
                          "flex w-full items-center gap-4",
                          !(
                            (showSelectedItem && isSelected) ||
                            listItemLeftIcon
                          )
                            ? "pl-9"
                            : null,
                        )}
                      >
                        {showSelectedItem && isSelected ? (
                          <Check className="w-4" />
                        ) : null}
                        {listItemLeftIcon}
                        {option.label}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ) : null}
              {!isLoading && showNoResult && emptyMessage ? (
                <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                  {emptyMessage}
                </CommandPrimitive.Empty>
              ) : null}
            </CommandList>
          </div>
        </div>
      </ClientPortal>
    </CommandPrimitive>
  );
};
